import React, { useEffect, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonContent,
    IonLabel, IonInput, IonButton, IonIcon, useIonAlert
} from '@ionic/react';
import { trashOutline, addCircleOutline } from 'ionicons/icons';

import {
    actionToGetEventCombos,
    actionToCreateEventCombo,
    actionToDeleteEventCombo,
    actionToGetAllEventsLite
} from '../apiHelpers/CommonAction';

const EventComboModal = ({ isOpen, eventId, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [combos, setCombos] = useState([]);
    const [eventsLite, setEventsLite] = useState([]);

    const [comboName, setComboName] = useState('');
    const [selectedEventIds, setSelectedEventIds] = useState([]); // array of ids
    const [priceCA, setPriceCA] = useState('');
    const [priceMember, setPriceMember] = useState('');
    const [priceOther, setPriceOther] = useState('');
    const [presentAlert] = useIonAlert();

    const resetForm = () => {
        setComboName('');
        setSelectedEventIds(eventId ? [eventId] : []);
        setPriceCA('');
        setPriceMember('');
        setPriceOther('');
    };

    const loadAll = async () => {
        setLoading(true);
        try {
            const [evResp, comboResp] = await Promise.all([
                actionToGetAllEventsLite(),
                actionToGetEventCombos(eventId) // if eventId present, view combos that include it
            ]);
            setEventsLite(evResp || []);
            setCombos(comboResp || []);
            // preselect current event on open (for convenience)
            setSelectedEventIds(eventId ? [eventId] : []);
        } catch (e) {
            // noop
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadAll();
            resetForm();
        }
    }, [isOpen, eventId]);

    const toggleEventId = (id) => {
        setSelectedEventIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const createCombo = async () => {
        const missing = [];
        if (!comboName.trim()) missing.push('Combo name');
        if (selectedEventIds.length < 2) missing.push('Select at least two events');
        if (priceCA === '') missing.push('CA price');
        if (priceMember === '') missing.push('Member price');
        if (priceOther === '') missing.push('Other price');

        if (missing.length) {
            presentAlert({ header: 'Missing', message: missing.join(' '), buttons: ['OK'] });
            return;
        }

        setLoading(true);
        try {
            await actionToCreateEventCombo({
                combo_name: comboName.trim(),
                event_ids: selectedEventIds, // backend supports array -> CSV
                price_for_ca: priceCA,
                price_for_member: priceMember,
                price_for_other: priceOther
            });
            await loadAll();
            resetForm();
        } catch (e) {
            presentAlert({ header: 'Create failed', message: e?.message || 'Please try again.', buttons: ['OK'] });
        } finally {
            setLoading(false);
        }
    };

    const deleteCombo = (id) => {
        presentAlert({
            header: 'Delete combo?',
            message: 'This will remove the combo.',
            buttons: [
                { text: 'Cancel', role: 'cancel' },
                {
                    text: 'Delete', role: 'destructive', handler: async () => {
                        setLoading(true);
                        try {
                            await actionToDeleteEventCombo(id);
                            await loadAll();
                        } catch (e) {
                            presentAlert({ header: 'Delete failed', message: e?.message || 'Please try again.', buttons: ['OK'] });
                        } finally {
                            setLoading(false);
                        }
                    }
                }
            ]
        });
    };

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onClose}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Manage Event Combos</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
                {/* Create form */}
                <div className="combo_create_box">
                    <h3>Create Combo</h3>

                    <div className="grid-2">
                        <div>
                            <IonLabel>Combo Name *</IonLabel>
                            <IonInput
                                className="rfp_form_input"
                                placeholder="e.g., Income Tax + FPG Meet"
                                value={comboName}
                                onIonInput={(e) => setComboName(e.detail.value || '')}
                            />
                        </div>

                        <div className="price_grid">
                            <div>
                                <IonLabel>CA Price *</IonLabel>
                                <IonInput type="number" inputMode="decimal" value={priceCA}
                                          onIonInput={(e) => setPriceCA(e.detail.value || '')} />
                            </div>
                            <div>
                                <IonLabel>Member Price *</IonLabel>
                                <IonInput type="number" inputMode="decimal" value={priceMember}
                                          onIonInput={(e) => setPriceMember(e.detail.value || '')} />
                            </div>
                            <div>
                                <IonLabel>Other Price *</IonLabel>
                                <IonInput type="number" inputMode="decimal" value={priceOther}
                                          onIonInput={(e) => setPriceOther(e.detail.value || '')} />
                            </div>
                        </div>
                    </div>

                    <div className="events_picker">
                        <IonLabel>Pick Events (select at least 2)</IonLabel>
                        <div className="events_list">
                            {eventsLite.map(ev => (
                                <label key={ev.id} className="event_chip">
                                    <input
                                        type="checkbox"
                                        checked={selectedEventIds.includes(ev.id)}
                                        onChange={() => toggleEventId(ev.id)}
                                    />
                                    <span>{ev.title}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <IonButton onClick={createCombo} disabled={loading}>
                        <IonIcon slot="start" icon={addCircleOutline} />
                        Create Combo
                    </IonButton>
                </div>

                <hr />

                {/* Existing combos */}
                <div className="combo_list_box">
                    <h3>Existing Combos {eventId ? '(including this event)' : ''}</h3>
                    {combos.length === 0 ? (
                        <p>No combos found.</p>
                    ) : (
                        <div className="combo_cards">
                            {combos.map(c => (
                                <div className="combo_card" key={c.id}>
                                    <div className="combo_header">
                                        <div className="combo_name">{c.combo_name}</div>
                                        <button className="combo_del_btn" onClick={() => deleteCombo(c.id)}>
                                            <IonIcon icon={trashOutline} />
                                        </button>
                                    </div>
                                    <div className="combo_events">{c.included_events || '(no events resolved)'}</div>
                                    <div className="combo_prices">
                                        <span>CA: {c.price_for_ca}</span>
                                        <span>Member: {c.price_for_member}</span>
                                        <span>Other: {c.price_for_other}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="editor-actions">
                    <IonButton expand="block" onClick={onClose} disabled={loading}>Close</IonButton>
                </div>
            </IonContent>
        </IonModal>
    );
};

export default EventComboModal;